// VendasAdmin.js
"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import {
  FaEye,
  FaPrint,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaFileInvoiceDollar,
  FaEdit,
  FaHashtag,
  FaGift,
  FaPaperPlane,
  FaExchangeAlt,
  FaTicketAlt
} from "react-icons/fa"
import jsPDF from "jspdf"
import "jspdf-autotable"
import JsBarcode from "jsbarcode"
import "../styles/vendasAdmin.css"

const ROTULOS_PAGAMENTO = {
  pix: "Pix",
  boleto: "Boleto",
  boleto_parcelado: "Boleto Parcelado",
  "boleto-parcelado": "Boleto Parcelado",
  "boleto parcelado": "Boleto Parcelado",
  bank_slip: "Boleto",
  cartao: "Cartão",
  visa: "Cartão",
  master: "Cartão",
  elo: "Cartão",
  amex: "Cartão",
  hipercard: "Cartão",
  dinheiro: "Dinheiro",
  cheque: "Cheque",
  vale: "Vale",
  transferencia: "Transferência",
  manual: "Manual",
};

function normalizarPagamento(valor) {
  return String(valor || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function obterFormasPagamento(pedido = {}) {
  const pagamento = pedido?.pagamento || {};
  const totalPedido = Number(pedido?.total) || 0;

  if (Array.isArray(pagamento?.formas) && pagamento.formas.length) {
    const formas = pagamento.formas
      .map((item) => ({
        forma: normalizarPagamento(item?.forma ?? item?.metodo ?? item?.tipo),
        valor: Number(item?.valor ?? item?.quantia) || 0,
      }))
      .filter((item) => item.forma && item.valor > 0);

    // Pedidos antigos do site podiam ficar apenas com isVale=true, sem que a
    // parcela Vale fosse escrita no rateio. Para a interface, tratamos o total
    // como vale até que a nova rota normalize definitivamente o documento.
    if (pedido?.isVale === true && !formas.some((item) => item.forma === "vale") && totalPedido > 0) {
      return [{ forma: "vale", valor: totalPedido }];
    }

    if (formas.length) return formas;
  }

  const legadas = [
    { forma: pagamento?.forma1, valor: pagamento?.valor1 },
    { forma: pagamento?.forma2, valor: pagamento?.valor2 },
    { forma: pagamento?.forma3, valor: pagamento?.valor3 },
  ]
    .map((item) => ({ forma: normalizarPagamento(item.forma), valor: Number(item.valor) || 0 }))
    .filter((item) => item.forma && item.valor > 0);

  if (pedido?.isVale === true && !legadas.some((item) => item.forma === "vale") && totalPedido > 0) {
    return [{ forma: "vale", valor: totalPedido }];
  }

  if (legadas.length) return legadas;

  const forma = normalizarPagamento(pedido?.metodoPagamento);
  return forma && totalPedido > 0 ? [{ forma, valor: totalPedido }] : [];
}

function pedidoPossuiVale(pedido = {}) {
  return Boolean(pedido?.isVale) || obterFormasPagamento(pedido).some((item) => item.forma === "vale");
}

function valorEmValeDoPedido(pedido = {}) {
  return obterFormasPagamento(pedido)
    .filter((item) => item.forma === "vale")
    .reduce((total, item) => total + (Number(item.valor) || 0), 0);
}

function valorComercialValeParqueDoPedido(pedido = {}) {
  const original = Number(pedido?.valorOriginalAntesValeParque) || 0;
  const total = Number(pedido?.total) || 0;
  const restanteParque = Number(pedido?.valorValeParqueRestante) || 0;
  return original > 0 ? original : total + restanteParque;
}

function valorValeParqueRestanteDoPedido(pedido = {}) {
  return Number(pedido?.valorValeParqueRestante) || 0;
}

function rotuloFormasPagamento(pedido) {
  const formas = obterFormasPagamento(pedido);
  if (!formas.length) return "Não informado";
  return [...new Set(formas.map((item) => ROTULOS_PAGAMENTO[item.forma] || item.forma))].join(" / ");
}

function rateioFormasPagamento(pedido) {
  const formas = obterFormasPagamento(pedido);
  if (!formas.length) return "Não informado";
  return formas
    .map((item) => `${ROTULOS_PAGAMENTO[item.forma] || item.forma}: R$ ${item.valor.toFixed(2)}`)
    .join(" | ");
}

function statusVisualPagamento(pedido) {
  const status = String(pedido?.statusPagamento || "").toLowerCase();
  if (status === "pago" && normalizarPagamento(pedido?.metodoPagamento) === "manual") {
    return "MANUAL";
  }
  return status ? status.toUpperCase() : "DESCONHECIDO";
}


function normalizarOrigem(valor) {
  return String(valor || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * A origem é definida pelo fluxo que criou o pedido:
 * - programa/desktop/offline/presencial => venda feita na secretaria;
 * - site/online/web/antecipado => compra feita no site.
 *
 * Pedidos históricos sem origem foram criados antes do desktop híbrido e,
 * por compatibilidade, são exibidos como antecipados.
 */
function obterOrigemPedido(pedido = {}) {
  const origem = normalizarOrigem(pedido?.origem);

  if (["programa", "presencial", "desktop", "offline", "secretaria", "pdv", "local", "venda-presencial"].includes(origem)) {
    return { rotulo: "Presencial", classe: "presencial", chave: "presencial" };
  }

  if (["site", "online", "antecipado", "web", "checkout", "boleto-parcelado"].includes(origem)) {
    return { rotulo: "Online / Site", classe: "antecipado", chave: "online" };
  }

  return { rotulo: "Online / Site", classe: "antecipado", chave: "online" };
}

function formaEhCartao(forma) {
  return [
    "cartao",
    "cartao credito",
    "cartao de credito",
    "credito",
    "credit_card",
    "visa",
    "master",
    "mastercard",
    "elo",
    "amex",
    "hipercard",
  ].includes(normalizarPagamento(forma));
}

function formaEhBoleto(forma) {
  return [
    "boleto",
    "boleto parcelado",
    "boleto_parcelado",
    "boleto-parcelado",
    "bank_slip",
    "bankslip",
    "asaas boleto",
  ].includes(normalizarPagamento(forma));
}

function pedidoTemFormaPagamento(pedido = {}, filtro = "") {
  const alvo = normalizarPagamento(filtro);
  if (!alvo) return true;

  const formas = obterFormasPagamento(pedido).map((item) => normalizarPagamento(item.forma));
  const metodoPrincipal = normalizarPagamento(pedido?.metodoPagamento);
  const todasFormas = [...formas, metodoPrincipal].filter(Boolean);

  if (alvo === "vale") {
    return pedidoPossuiVale(pedido);
  }

  if (alvo === "manual") {
    return todasFormas.includes("manual");
  }

  if (alvo === "cartao") {
    return todasFormas.some((forma) => formaEhCartao(forma));
  }

  if (alvo === "boleto") {
    return todasFormas.some((forma) => formaEhBoleto(forma));
  }

  return todasFormas.includes(alvo);
}

const VendasAdmin = () => {
  const [pedidos, setPedidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [atualizando, setAtualizando] = useState(false)
  const [paginaAtual, setPaginaAtual] = useState(1)
  const [busca, setBusca] = useState("")
  const [buscaId, setBuscaId] = useState("")
  const [buscaIdDebounced, setBuscaIdDebounced] = useState("")
  const [buscaDebounced, setBuscaDebounced] = useState("")
  const [totalPaginas, setTotalPaginas] = useState(1)
  const [totalPedidos, setTotalPedidos] = useState(0)
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState([])
  const primeiraCargaRef = useRef(true)
  const requisicaoPedidosRef = useRef(0)
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null)
  const [pedidoDesconto, setPedidoDesconto] = useState(null)
  const [codigoDesconto, setCodigoDesconto] = useState("")
  const [salvandoDesconto, setSalvandoDesconto] = useState(false)
  const [pedidoAprovacaoManual, setPedidoAprovacaoManual] = useState(null)
  const [formaAprovacaoManual, setFormaAprovacaoManual] = useState("pix")
  const [salvandoAprovacaoManual, setSalvandoAprovacaoManual] = useState(false)
  const [filtroStatus, setFiltroStatus] = useState("pendente")
  const [filtroEvento, setFiltroEvento] = useState("")
  const [filtroOrigemPedido, setFiltroOrigemPedido] = useState("")
  const [filtroFormaPagamento, setFiltroFormaPagamento] = useState("")
  const [filtroCategoria, setFiltroCategoria] = useState("")
  const [eventos, setEventos] = useState([])
  const itensPorPagina = 10

  // edição infos
  const [pedidoEditando, setPedidoEditando] = useState(null)

  // edição senhas
  const [pedidoEditSenhas, setPedidoEditSenhas] = useState(null)
  const [novasSenhas, setNovasSenhas] = useState([])
  // Troca de Categoria
  const [pedidoTrocaCategoria, setPedidoTrocaCategoria] = useState(null);
  const [novaCategoriaSelecionada, setNovaCategoriaSelecionada] = useState("");
  const [erroSenhas, setErroSenhas] = useState("")

  // 👉 NOVO: modal Vale
  const [pedidoVale, setPedidoVale] = useState(null)
  const [valeDescricao, setDescricaoVale] = useState("")
  const [tipoValeCriar, setTipoValeCriar] = useState("comum")
  const [valorValeParque, setValorValeParque] = useState("")
  const [novaFormaVale, setNovaFormaVale] = useState("pix")
  const [valorRemoverVale, setValorRemoverVale] = useState("")
  const [tipoValeRemover, setTipoValeRemover] = useState("comum")
  const [motivoRemoverVale, setMotivoRemoverVale] = useState("")

  // -------------------------------
  // Busca com debounce: evita uma requisição ao MongoDB a cada tecla digitada.
  // -------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setBuscaDebounced(busca.trim())
    }, 450)

    return () => clearTimeout(timer)
  }, [busca])

  useEffect(() => {
    const timer = setTimeout(() => setBuscaIdDebounced(buscaId.trim()), 450)
    return () => clearTimeout(timer)
  }, [buscaId])

  // -------------------------------
  // Carregar pedidos - paginação e filtros executados diretamente no MongoDB.
  // -------------------------------
  const carregarPedidos = useCallback(async ({ carregamentoInicial = false } = {}) => {
    const requisicaoAtual = ++requisicaoPedidosRef.current

    try {
      if (carregamentoInicial) {
        setLoading(true)
      } else {
        setAtualizando(true)
      }

      const token = localStorage.getItem("token")
      const response = await axios.get(
        "https://api.ticketdovaqueiro.com/api/pedidos/todos-pedidos-admin",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            paginado: true,
            pagina: paginaAtual,
            limite: itensPorPagina,
            busca: buscaDebounced || undefined,
            pedidoId: buscaIdDebounced || undefined,
            status: filtroStatus || undefined,
            eventoId: filtroEvento || undefined,
            categoria: filtroCategoria || undefined,
            origem: filtroOrigemPedido || undefined,
            formaPagamento: filtroFormaPagamento || undefined,
          },
        }
      )

      // Ignora respostas antigas caso o usuário altere filtros rapidamente.
      if (requisicaoAtual !== requisicaoPedidosRef.current) return

      const dados = response.data || {}
      const lista = Array.isArray(dados.pedidos) ? dados.pedidos : []
      const paginacao = dados.paginacao || {}

      setPedidos(lista)
      setTotalPedidos(Number(paginacao.total) || 0)
      setTotalPaginas(Math.max(Number(paginacao.totalPaginas) || 1, 1))
      setCategoriasDisponiveis(Array.isArray(dados?.filtros?.categorias) ? dados.filtros.categorias : [])

      // Pode acontecer após cancelar/excluir o último item de uma página.
      const ultimaPagina = Math.max(Number(paginacao.totalPaginas) || 1, 1)
      if (paginaAtual > ultimaPagina) {
        setPaginaAtual(ultimaPagina)
      }
    } catch (error) {
      if (requisicaoAtual !== requisicaoPedidosRef.current) return
      console.error("❌ Erro ao buscar pedidos:", error)
      alert("Erro ao atualizar pedidos. Veja o console para detalhes.")
    } finally {
      if (requisicaoAtual === requisicaoPedidosRef.current) {
        if (carregamentoInicial) {
          setLoading(false)
        } else {
          setAtualizando(false)
        }
      }
    }
  }, [
    paginaAtual,
    buscaDebounced,
    buscaIdDebounced,
    filtroStatus,
    filtroEvento,
    filtroCategoria,
    filtroOrigemPedido,
    filtroFormaPagamento,
  ])

  useEffect(() => {
    const carregamentoInicial = primeiraCargaRef.current
    primeiraCargaRef.current = false
    carregarPedidos({ carregamentoInicial })
  }, [carregarPedidos])

  // -------------------------------
  // Carregar eventos (filtro)
  // -------------------------------
  useEffect(() => {
    const carregarEventos = async () => {
      try {
        const token = localStorage.getItem("token")
        const response = await axios.get("https://api.ticketdovaqueiro.com/api/events", {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (Array.isArray(response.data)) setEventos(response.data)
      } catch (error) {
        console.error("❌ Erro ao buscar eventos:", error)
      }
    }
    carregarEventos()
  }, [])

  // -------------------------------
  // Filtros / paginação
  // Os filtros são aplicados no backend. O navegador recebe apenas a página atual.
  // -------------------------------
  useEffect(() => {
    setPaginaAtual(1)
  }, [buscaDebounced, buscaIdDebounced, filtroStatus, filtroEvento, filtroCategoria, filtroOrigemPedido, filtroFormaPagamento])

  useEffect(() => {
    if (!filtroCategoria) return

    const categoriaAindaExiste = categoriasDisponiveis.some(
      (categoria) => String(categoria).toLowerCase() === String(filtroCategoria).toLowerCase()
    )

    if (!categoriaAindaExiste) {
      setFiltroCategoria("")
    }
  }, [categoriasDisponiveis, filtroCategoria])

  const pedidosPaginados = pedidos

  const avancarPagina = () => {
    if (paginaAtual < totalPaginas) setPaginaAtual((pagina) => pagina + 1)
  }
  const voltarPagina = () => {
    if (paginaAtual > 1) setPaginaAtual((pagina) => pagina - 1)
  }
  const handleSenhaCampoChange = (index, campo, valor) => {
    setPedidoEditando((prev) => {
      if (!prev) return prev;
      const senhas = Array.isArray(prev.senhas) ? [...prev.senhas] : [];
      const senha = { ...senhas[index] };

      const dvAtual = senha.dadosVaqueiro || {};
      senha.dadosVaqueiro = { ...dvAtual, [campo]: valor };

      senhas[index] = senha;
      return { ...prev, senhas };
    });
  };

  // -------------------------------
  // Ações já existentes
  // -------------------------------
  const abrirModal = (pedido) => setPedidoSelecionado(pedido)
  const fecharModal = () => setPedidoSelecionado(null)

  const alterarStatusPagamento = async (pedidoId, novoStatus, formaPagamentoManual = null) => {
    try {
      const token = localStorage.getItem("token")
      const payload = { statusPagamento: novoStatus }
      if (novoStatus === "manual" && formaPagamentoManual) {
        payload.formaPagamentoManual = formaPagamentoManual
      }

      const resposta = await axios.put(
        `https://api.ticketdovaqueiro.com/api/pedidos/alterar-status/${pedidoId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const pedidoAtualizado = resposta?.data?.pedido || {}
      alert(novoStatus === "manual" ? "Pagamento aprovado manualmente com sucesso!" : "Status atualizado com sucesso!")
      setPedidos((prev) =>
        prev.map((p) => (p._id === pedidoId ? { ...p, ...pedidoAtualizado } : p))
      )
      return true
    } catch (err) {
      console.error("Erro ao alterar status:", err)
      alert(err?.response?.data?.mensagem || "Erro ao alterar status.")
      return false
    }
  }

  const confirmarReembolsoManual = async (pedido) => {
    if (!pedido?._id || pedido?.solicitacaoReembolso?.status !== "solicitado_suporte") return
    const confirmado = window.confirm(
      `Confirma que o estorno do pedido de ${pedido.nomeVaqueiro || "cliente"} já foi concluído na InfinitePay/Asaas? Só confirme depois que o dinheiro tiver sido devolvido.`
    )
    if (!confirmado) return

    try {
      const token = localStorage.getItem("token")
      const resposta = await axios.patch(
        `https://api.ticketdovaqueiro.com/api/pedidos/${pedido._id}/reembolso/confirmar-manual`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const atualizado = {
        ...pedido,
        status: "cancelado",
        statusPagamento: "reembolsado",
        solicitacaoReembolso: { ...pedido.solicitacaoReembolso, status: "concluido", concluidoEm: new Date().toISOString() },
      }
      setPedidos((anteriores) => anteriores.map((item) => item._id === pedido._id ? { ...item, ...atualizado } : item))
      setPedidoSelecionado(atualizado)
      alert(resposta.data?.mensagem || "Reembolso confirmado e senhas liberadas.")
    } catch (err) {
      console.error("Erro ao confirmar reembolso manual:", err)
      alert(err?.response?.data?.mensagem || "Não foi possível confirmar o reembolso.")
    }
  }

  const cancelarSenhaIndividual = async (pedido, senha) => {
    const senhasAtivas = (pedido.senhas || []).filter((item) => !["cancelada", "disponivel"].includes(String(item.status || "").toLowerCase()))
    const ultimaSenha = senhasAtivas.length <= 1
    const motivo = window.prompt(`Motivo do cancelamento da senha ${senha.numero} (opcional):`, "")
    if (motivo === null) return
    const entradaTotal = window.prompt(
      `Digite o novo valor TOTAL do pedido que ficará após cancelar a senha ${senha.numero}.${ultimaSenha ? " Como esta é a última senha, informe 0,00." : ""}\n\nValor atual do pedido: R$ ${Number(pedido.total || 0).toFixed(2)}\nExemplo: se o pedido era R$ 420,00 e deve ficar R$ 210,00, informe 210,00.`,
      ultimaSenha ? "0,00" : ""
    )
    if (entradaTotal === null) return
    const textoTotal = String(entradaTotal).trim().replace(/[^\d,.-]/g, "")
    const normalizadoTotal = textoTotal.includes(",")
      ? textoTotal.replace(/\./g, "").replace(",", ".")
      : (/^\d{1,3}(\.\d{3})+$/.test(textoTotal) ? textoTotal.replace(/\./g, "") : textoTotal)
    const novoTotal = Number(normalizadoTotal)
    const totalAtual = Number(pedido.total) || 0
    if (!Number.isFinite(novoTotal) || novoTotal < 0 || novoTotal > totalAtual || (ultimaSenha && novoTotal !== 0)) {
      alert(ultimaSenha ? "Ao cancelar a última senha, o novo total deve ser R$ 0,00." : "Informe um valor válido entre R$ 0,00 e o valor atual do pedido.")
      return
    }
    const estornoPrevisto = pedido.statusPagamento === "pago" || pedido.statusPagamento === "transferencia"
      ? Math.max(0, totalAtual - novoTotal) : 0
    const confirma = window.confirm(
      `Confirma cancelar somente a senha ${senha.numero}?\n\nNovo total do pedido: R$ ${novoTotal.toFixed(2)}${estornoPrevisto > 0 ? `\nEstorno a realizar: R$ ${estornoPrevisto.toFixed(2)}\n\nO estorno financeiro deverá ser feito pela equipe.` : ""}`
    )
    if (!confirma) return
    try {
      const token = localStorage.getItem("token")
      const resposta = await axios.patch(
        `https://api.ticketdovaqueiro.com/api/pedidos/${pedido._id}/senhas/cancelar`,
        { numero: senha.numero, motivo, novoTotal },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const atualizado = resposta.data?.pedido
      if (atualizado) {
        setPedidos((anteriores) => anteriores.map((item) => item._id === pedido._id ? { ...item, ...atualizado } : item))
        setPedidoSelecionado((anterior) => anterior?._id === pedido._id ? { ...anterior, ...atualizado } : anterior)
      }
      alert(resposta.data?.mensagem || "Senha cancelada.")
    } catch (err) {
      alert(err?.response?.data?.mensagem || "Não foi possível cancelar esta senha.")
    }
  }

  const abrirAprovacaoManual = (pedido) => {
    const ehParcelado = Boolean(pedido?.detalhesPagamento?.parcelado) ||
      String(pedido?.transacaoId || "").startsWith("parcelado_") ||
      ["boleto_parcelado", "boleto-parcelado", "boleto parcelado"].includes(normalizarPagamento(pedido?.metodoPagamento))
    setFormaAprovacaoManual(ehParcelado ? "boleto_parcelado" : "pix")
    setPedidoAprovacaoManual(pedido)
  }

  const confirmarAprovacaoManual = async () => {
    if (!pedidoAprovacaoManual?._id || salvandoAprovacaoManual) return
    setSalvandoAprovacaoManual(true)
    const ok = await alterarStatusPagamento(
      pedidoAprovacaoManual._id,
      "manual",
      formaAprovacaoManual
    )
    setSalvandoAprovacaoManual(false)
    if (ok) setPedidoAprovacaoManual(null)
  }
 // -------------------------------
  // Enviar comprovante via WhatsApp (Z-API)
  // -------------------------------
  const enviarComprovanteWhatsapp = async (pedidoId) => {
    if (!pedidoId) return

    try {
      const token = localStorage.getItem("token")

      const resp = await axios.post(
        "https://api.ticketdovaqueiro.com/api/testezapi/testar-envio-zapi",
        { pedidoId },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (resp.data?.success) {
        alert("✅ Comprovante enviado via WhatsApp!")
      } else {
        alert("❌ Não foi possível enviar o comprovante via WhatsApp.")
      }
    } catch (err) {
      console.error("Erro ao enviar comprovante via WhatsApp:", err)
      alert("❌ Erro ao enviar o comprovante. Veja o console para detalhes.")
    }
  }
  // editar infos
  // editar infos
const abrirModalEdicao = (pedido) => {
  // garante que cada senha tem um objeto dadosVaqueiro,
  // usando como padrão os dados do pedido (para pedidos antigos)
  const senhasEnriquecidas = Array.isArray(pedido.senhas)
    ? pedido.senhas.map((s) => ({
        ...s,
        dadosVaqueiro: {
          apelido: s.dadosVaqueiro?.apelido ?? s.apelido ?? pedido.apelido ?? "",
          nomeEsteira:
            s.dadosVaqueiro?.nomeEsteira ?? pedido.nomeEsteira ?? "",
          cavaloPuxar:
            s.dadosVaqueiro?.cavaloPuxar ?? pedido.cavaloPuxar ?? "",
          cavaloEsteira:
            s.dadosVaqueiro?.cavaloEsteira ?? pedido.cavaloEsteira ?? "",
          representacao:
            s.dadosVaqueiro?.representacao ?? pedido.representacao ?? "",
          cidade: s.dadosVaqueiro?.cidade ?? pedido.cidade ?? "",
        },
      }))
    : [];

  setPedidoEditando({
    ...pedido,
    senhas: senhasEnriquecidas,
  });
};

const fecharModalEdicao = () => setPedidoEditando(null);



  // editar senhas
// editar senhas
const abrirModalEditarSenhas = (pedido) => {
  // esse state é quem controla a abertura do modal
  setPedidoEditSenhas(pedido);

  // já entra com as senhas atuais preenchidas
  setNovasSenhas(
    Array.isArray(pedido.senhas)
      ? pedido.senhas.map((s) => String(s.numero))
      : []
  );

  // zera mensagem de erro
  setErroSenhas("");
};

  const fecharModalEditarSenhas = () => {
    setPedidoEditSenhas(null)
    setNovasSenhas([])
    setErroSenhas("")
  }

  const aplicarDescontoAdmin = async () => {
    if (!pedidoDesconto || !codigoDesconto.trim()) return alert("Informe o código do cupom.")
    setSalvandoDesconto(true)
    try {
      const token = localStorage.getItem("token")
      const { data } = await axios.post(
        `https://api.ticketdovaqueiro.com/api/pedidos/${pedidoDesconto._id}/aplicar-cupom-desconto-admin`,
        { codigo: codigoDesconto },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const atualizado = data.pedido
      setPedidos((prev) => prev.map((p) => p._id === atualizado._id ? { ...p, ...atualizado } : p))
      setPedidoSelecionado((prev) => prev?._id === atualizado._id ? { ...prev, ...atualizado } : prev)
      setPedidoDesconto(null)
      setCodigoDesconto("")
      alert(`Cupom aplicado. Novo valor líquido: R$ ${Number(atualizado.total || 0).toFixed(2)}`)
    } catch (err) {
      alert(err?.response?.data?.mensagem || "Não foi possível aplicar o cupom ao pedido.")
    } finally { setSalvandoDesconto(false) }
  }
  const salvarNovasSenhas = async () => {
    const parsed = novasSenhas.map((s) => parseInt(String(s).replace(/\D/g, ""), 10))
    if (parsed.some((n) => !Number.isInteger(n) || n <= 0)) {
      setErroSenhas("Todas as senhas devem ser números positivos.")
      return
    }
    const setU = new Set(parsed)
    if (setU.size !== parsed.length) {
      setErroSenhas("Existem números repetidos.")
      return
    }
    try {
      const token = localStorage.getItem("token")
      const resp = await axios.put(
        `https://api.ticketdovaqueiro.com/api/pedidos/editar-senhas/${pedidoEditSenhas._id}`,
        { novasSenhas: parsed },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const pedidoAtualizado = resp.data.pedido || resp.data
      setPedidos((prev) =>
        prev.map((p) => (p._id === pedidoAtualizado._id ? { ...p, senhas: pedidoAtualizado.senhas } : p))
      )
      alert("Senhas atualizadas!")
      fecharModalEditarSenhas()
    } catch (err) {
      const msg = err?.response?.data?.mensagem || "Erro ao atualizar senhas."
      setErroSenhas(msg)
    }
  }
  const salvarTrocaCategoria = async () => {
    if (!novaCategoriaSelecionada) return alert("Selecione a nova categoria.");
    if (novaCategoriaSelecionada === pedidoTrocaCategoria.categoria) return alert("A categoria selecionada é igual à atual.");

    if (!window.confirm(`ATENÇÃO! Isso irá alterar a categoria, atualizar as senhas e RECALCULAR o total do pedido baseado nos preços da nova categoria. Se o pedido já estava pago, você deverá cobrar a diferença do vaqueiro por fora.\n\nDeseja continuar?`)) return;

    try {
      const res = await axios.put(`https://api.ticketdovaqueiro.com/api/pedidos/${pedidoTrocaCategoria._id}/mudar-categoria`, {
        novaCategoria: novaCategoriaSelecionada
      });
      
      const pedidoAtualizado = res.data?.pedido;
      const novoTotal = pedidoAtualizado?.total ?? 0;
      alert(`Sucesso! Novo total do pedido: R$ ${novoTotal}`);

      if (pedidoAtualizado?._id) {
        setPedidos((prev) =>
          prev.map((p) => (p._id === pedidoAtualizado._id ? { ...p, ...pedidoAtualizado } : p))
        );
      }

      setPedidoTrocaCategoria(null);
      setNovaCategoriaSelecionada("");
    } catch (e) {
      alert(e.response?.data?.erro || "Erro ao trocar categoria.");
    }
  };
  // -------------------------------
  // PDF (já existia)
  // -------------------------------
  const gerarCodigoAutenticacao = (pedidoId) =>
    `AUTH-${pedidoId.slice(-6)}-${Math.floor(1000 + Math.random() * 9000)}`

  const gerarPDF = (pedido) => {
    const doc = new jsPDF()
    const codigoAutenticacao = gerarCodigoAutenticacao(pedido._id)

    try {
      const img = new Image()
      img.src = "/placeholder.svg?height=60&width=150"
      img.crossOrigin = "anonymous"
      doc.addImage(img, "PNG", 10, 10, 40, 20)
    } catch {}

    doc.setFontSize(18)
    doc.text("Detalhes do Pedido", 80, 20)

    doc.setFontSize(12)
    doc.text(`Nome: ${pedido.usuario?.nomeCompleto || "Não informado"}`, 14, 40)
    doc.text(`Email: ${pedido.usuario?.email || "Não informado"}`, 14, 48)
    doc.text(`Evento: ${pedido.evento?.nome || "Não informado"}`, 14, 56)
    doc.text(`Categoria: ${pedido.categoria || "Não informado"}`, 14, 64)
    doc.text(`Forma(s) de Pagamento: ${rotuloFormasPagamento(pedido)}`, 14, 72)
    doc.text(`Rateio: ${rateioFormasPagamento(pedido)}`, 14, 80)
    doc.text(`Valor Pago: R$ ${pedido.total?.toFixed(2) || "0.00"}`, 14, 88)
    doc.text(
      `Status do Pagamento: ${statusVisualPagamento(pedido)}`,
      14,
      96
    )
    doc.text(`Nome do Vaqueiro: ${pedido.nomeVaqueiro || "Não informado"}`, 14, 104)
    doc.text(`Representação: ${pedido.representacao || "Não informado"}`, 14, 112)
    doc.text(`Cidade: ${pedido.cidade || "Não informado"}`, 14, 120)
    doc.text(`Cavalo de Puxada: ${pedido.cavaloPuxar || "Não informado"}`, 14, 128)
    doc.text(`Cavalo de Esteira: ${pedido.cavaloEsteira || "Não informado"}`, 14, 136)
    doc.text(`Nome do Esteira: ${pedido.nomeEsteira || "Não informado"}`, 14, 144)

    doc.setFontSize(10)
    doc.text(`Código de Autenticação: ${codigoAutenticacao}`, 14, 152)

    try {
      const canvas = document.createElement("canvas")
      JsBarcode(canvas, codigoAutenticacao, { format: "CODE128" })
      const barcodeImage = canvas.toDataURL("image/png")
      doc.addImage(barcodeImage, "PNG", 14, 150, 80, 20)
    } catch {}

    doc.save(`Pedido_${pedido._id}.pdf`)
  }

  // -------------------------------
  // NOVO: Modal Vale (com descrição)
  // -------------------------------
  const abrirModalVale = (pedido) => {
    setPedidoVale(pedido)
    setDescricaoVale(pedido?.valeDescricao || "")
    setTipoValeCriar("comum")
    setValorValeParque("")
    setNovaFormaVale("pix")
    setValorRemoverVale("")
    setTipoValeRemover(valorEmValeDoPedido(pedido) > 0 ? "comum" : "parque")
    setMotivoRemoverVale("")
  }

  const fecharModalVale = () => {
    setPedidoVale(null)
    setDescricaoVale("")
    setTipoValeCriar("comum")
    setValorValeParque("")
    setNovaFormaVale("pix")
    setValorRemoverVale("")
    setTipoValeRemover("comum")
    setMotivoRemoverVale("")
  }

  const salvarVale = async () => {
    if (!pedidoVale?._id) return;

    const removendoVale = pedidoPossuiVale(pedidoVale);
    if (!removendoVale && !String(valeDescricao || "").trim()) {
      alert("Informe a descrição do vale.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const endpoint = removendoVale
        ? `https://api.ticketdovaqueiro.com/api/pedidos/remover-vale/${pedidoVale._id}`
        : `https://api.ticketdovaqueiro.com/api/pedidos/marcar-vale/${pedidoVale._id}`;

      const payload = removendoVale
        ? {
            novaForma: novaFormaVale,
            valorRemover: valorRemoverVale,
            tipoValeRemover,
            motivo: motivoRemoverVale,
            usuario: "Admin",
          }
        : {
            descricao: String(valeDescricao || "").trim(),
            tipoVale: tipoValeCriar,
            valeDoParque: tipoValeCriar === "parque",
            valorValeParque: tipoValeCriar === "parque" ? valorValeParque : undefined,
            usuario: "Admin",
          };

      const resp = await axios.put(
        endpoint,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const atualizado = resp.data?.pedido;
      if (!atualizado?._id) {
        throw new Error("A API não retornou o pedido atualizado.");
      }

      setPedidos((prev) =>
        prev.map((p) => (p._id === atualizado._id ? { ...p, ...atualizado } : p))
      );
      fecharModalVale();
      alert(removendoVale ? "Vale removido e pagamento atualizado." : "Vale criado com sucesso.");
    } catch (err) {
      console.error("❌ Erro ao atualizar vale:", err);
      alert(
        err?.response?.data?.mensagem ||
        err?.response?.data?.erro ||
        err?.message ||
        "Não foi possível atualizar o vale."
      );
    }
  };

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <div className="jt-vendas-container">
      <h1>
        <FaFileInvoiceDollar /> Gestão de Pedidos
      </h1>

      <div className="jt-filtros-container">
        <input
          type="text"
          placeholder="Buscar por nome ou apelido do vaqueiro..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="jt-busca-input"
        />

        <input
          type="search"
          placeholder="Buscar por ID do pedido..."
          aria-label="Buscar por ID do pedido"
          value={buscaId}
          onChange={(e) => setBuscaId(e.target.value)}
          className="jt-busca-input jt-busca-id-input"
          spellCheck={false}
          autoComplete="off"
        />

        <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} className="jt-filtro-select">
          <option value="">Todos os Status</option>
          <option value="pendente">Pendente</option>
          <option value="pago">Pago</option>
          <option value="manual">Manual</option>
          <option value="processando">Processando</option>
          <option value="cancelado">Cancelado</option>
        </select>

        <select value={filtroEvento} onChange={(e) => setFiltroEvento(e.target.value)} className="jt-filtro-select">
          <option value="">Todos os Eventos</option>
          {eventos.map((evento) => (
            <option key={evento._id} value={evento._id}>
              {evento.nome}
            </option>
          ))}
        </select>

        <select
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
          className="jt-filtro-select"
        >
          <option value="">Todas as Categorias</option>
          {categoriasDisponiveis.map((categoria) => (
            <option key={categoria} value={categoria}>
              {categoria}
            </option>
          ))}
        </select>

        <select
          value={filtroOrigemPedido}
          onChange={(e) => setFiltroOrigemPedido(e.target.value)}
          className="jt-filtro-select"
        >
          <option value="">Todas as Origens</option>
          <option value="presencial">Presencial</option>
          <option value="online">Online / Site</option>
        </select>

        <select
          value={filtroFormaPagamento}
          onChange={(e) => setFiltroFormaPagamento(e.target.value)}
          className="jt-filtro-select"
        >
          <option value="">Todas as Formas</option>
          <option value="vale">Vale</option>
          <option value="manual">Manual</option>
          <option value="dinheiro">Dinheiro</option>
          <option value="pix">Pix</option>
          <option value="boleto">Boleto</option>
          <option value="boleto_parcelado">Boleto Parcelado</option>
          <option value="cartao">Cartão</option>
          <option value="cheque">Cheque</option>
        </select>

        <button
          type="button"
          onClick={() => carregarPedidos()}
          disabled={loading || atualizando}
          className="jt-btn-atualizar-pedidos"
          style={{
            minHeight: 42,
            padding: "0 16px",
            borderRadius: 8,
            border: "none",
            cursor: loading || atualizando ? "not-allowed" : "pointer",
            fontWeight: 700,
            background: "#f8c10b",
            color: "#111",
            opacity: loading || atualizando ? 0.65 : 1,
          }}
        >
          {atualizando ? "Atualizando..." : "Atualizar pedidos"}
        </button>
      </div>

      {loading ? (
        <div className="jt-loading-container">
          <div className="jt-loading-spinner"></div>
          <p>Carregando pedidos...</p>
        </div>
      ) : pedidos.length === 0 ? (
        <div className="jt-empty-state">
          <p>Nenhum pedido encontrado com os filtros selecionados.</p>
        </div>
      ) : (
        <>
          <div className="jt-table-container">
            <table className="jt-vendas-tabela">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Senhas</th>
                  <th>Evento</th>
                  <th>Origem</th>
                  <th>Valor Pago</th>
                  <th>Forma de Pagamento</th>
                  <th>Status</th>
                  <th>Vale?</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidosPaginados.map((pedido) => (
                  <tr key={pedido._id}>
                    <td>
                      <span>{pedido.nomeVaqueiro}</span>
                      {pedido.apelido && (
                        <><br /><small style={{ color: "#f8c10b", fontSize: "11px", fontStyle: "italic" }}>✦ {pedido.apelido}</small></>
                      )}
                    </td>
                    <td>{pedido.senhas?.map((s) => s.numero).join(", ")}</td>
                    <td>{pedido.evento?.nome || "Evento não encontrado"}</td>
                    <td>
                      <span className={`jt-origem-badge jt-origem-${obterOrigemPedido(pedido).classe}`}>
                        {obterOrigemPedido(pedido).rotulo}
                      </span>
                    </td>
                    <td>R$ {pedido.total?.toFixed(2) || "0.00"}</td>
                    <td title={rateioFormasPagamento(pedido)}>{rotuloFormasPagamento(pedido)}</td>
                    <td>
                      <span className={`jt-status-${pedido.statusPagamento}`}>
                        {statusVisualPagamento(pedido)}
                      </span>
                    </td>

                    {/* NOVO: indicador de vale */}
                    <td>
                      {pedidoPossuiVale(pedido) ? (
                        <span style={{ color: "green", fontWeight: 600 }}>✔ Vale</span>
                      ) : (
                        <span style={{ color: "gray" }}>—</span>
                      )}
                      {pedido.valeDescricao ? (
                        <div className="jt-vale-descricao" title={pedido.valeDescricao}>
                          {pedido.valeDescricao}
                        </div>
                      ) : null}
                    </td>

                    <td>{new Date(pedido.dataCriacao).toLocaleDateString()}</td>

                    <td className="jt-actions">
                      <FaEye className="jt-icon-action" onClick={() => abrirModal(pedido)} title="Ver detalhes" />
                      <FaTicketAlt className="jt-icon-action" onClick={() => { setPedidoDesconto(pedido); setCodigoDesconto("") }} title="Aplicar cupom de desconto" />
                      <FaEdit className="jt-icon-action" onClick={() => abrirModalEdicao(pedido)} title="Editar pedido" />
                      <FaPrint className="jt-icon-action" onClick={() => gerarPDF(pedido)} title="Gerar PDF" />
                      {/* Botão de Trocar Categoria */}
                      <FaExchangeAlt
                        className="icone-acao laranja"
                        title="Trocar Categoria (Upgrade)"
                        style={{ color: "#f39c12", cursor: "pointer", marginLeft: "10px" }}
                        onClick={() => {
                            setPedidoTrocaCategoria(pedido);
                            setNovaCategoriaSelecionada("");
                        }}
                      />
                      <FaHashtag
                        className="jt-icon-action"
                        onClick={() => abrirModalEditarSenhas(pedido)}
                        title="Editar números das senhas"
                      />
                        <FaPaperPlane
                          className="jt-icon-action"
                          onClick={() => enviarComprovanteWhatsapp(pedido._id)}
                          title="Enviar comprovante via WhatsApp"
                        />
                      {/* NOVO: Botão Vale */}
                      <FaGift
                        className="jt-icon-action"
                        style={{ color: pedidoPossuiVale(pedido) ? "green" : undefined }}
                        onClick={() => abrirModalVale(pedido)}
                        title={pedidoPossuiVale(pedido) ? "Remover Vale" : "Criar Vale"}
                      />
                      {/* atalhos de status */}
                      <span
                        style={{ color: "green", cursor: "pointer", marginLeft: 10 }}
                        title="Aprovar pagamento manualmente"
                        onClick={() => abrirAprovacaoManual(pedido)}
                      >
                        ✔️
                      </span>
                      <span
                        style={{ color: "red", cursor: "pointer", marginLeft: 10 }}
                        title="Cancelar Pedido"
                        onClick={() => alterarStatusPagamento(pedido._id, "cancelado")}
                      >
                        ❌
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="jt-pagination">
            <button onClick={voltarPagina} disabled={paginaAtual === 1}>
              <FaChevronLeft /> Anterior
            </button>
            <span>
              Página {paginaAtual} de {totalPaginas} · {totalPedidos} pedido{totalPedidos === 1 ? "" : "s"}
            </span>
            <button onClick={avancarPagina} disabled={paginaAtual === totalPaginas}>
              Próximo <FaChevronRight />
            </button>
          </div>
        </>
      )}


      {/* Modal de aprovação administrativa com forma real do pagamento */}
      {pedidoAprovacaoManual && (
        <div className="jt-modal-overlay">
          <div className="jt-modal-content">
            <FaTimes className="jt-close-icon" onClick={() => !salvandoAprovacaoManual && setPedidoAprovacaoManual(null)} />
            <h2>Aprovar pagamento</h2>
            <p>
              <strong>Cliente:</strong> {pedidoAprovacaoManual.nomeVaqueiro || "Não informado"}
            </p>
            <p>
              <strong>Valor:</strong> R$ {Number(pedidoAprovacaoManual.total || 0).toFixed(2)}
            </p>
            <div className="jt-form-group">
              <label>Forma real do pagamento</label>
              <select
                className="jt-input"
                value={formaAprovacaoManual}
                onChange={(e) => setFormaAprovacaoManual(e.target.value)}
                disabled={salvandoAprovacaoManual}
              >
                <option value="pix">Pix</option>
                <option value="cartao">Cartão</option>
                <option value="boleto">Boleto</option>
                <option value="boleto_parcelado">Boleto Parcelado</option>
                <option value="dinheiro">Dinheiro</option>
              </select>
            </div>
            {formaAprovacaoManual === "boleto_parcelado" && (
              <p style={{ fontSize: 13, opacity: 0.8 }}>
                Ao confirmar, o parcelamento vinculado também será marcado como concluído.
              </p>
            )}
            <button
              className="jt-btn-salvar"
              onClick={confirmarAprovacaoManual}
              disabled={salvandoAprovacaoManual}
            >
              {salvandoAprovacaoManual ? "Aprovando..." : "Confirmar aprovação"}
            </button>
          </div>
        </div>
      )}

      {pedidoDesconto && (
        <div className="jt-modal-overlay">
          <div className="jt-modal-content">
            <FaTimes className="jt-close-icon" onClick={() => !salvandoDesconto && setPedidoDesconto(null)} />
            <h2>Aplicar cupom ao pedido</h2>
            <p><strong>Vaqueiro:</strong> {pedidoDesconto.nomeVaqueiro || "Não informado"}</p>
            <p><strong>Senhas:</strong> {(pedidoDesconto.senhas || []).map((s) => s.numero).join(", ") || "—"}</p>
            <p><strong>Valor atual:</strong> R$ {Number(pedidoDesconto.total || 0).toFixed(2)}</p>
            {pedidoDesconto.desconto?.valor > 0 && <p><strong>Cupom atual:</strong> {pedidoDesconto.desconto.codigo} — desconto de R$ {Number(pedidoDesconto.desconto.valor).toFixed(2)}</p>}
            <div className="jt-form-group">
              <label>Código do cupom</label>
              <input className="jt-input" value={codigoDesconto} onChange={(e) => setCodigoDesconto(e.target.value.toUpperCase().replace(/\s+/g, ""))} maxLength={40} disabled={salvandoDesconto} placeholder="Ex.: ALOP" />
            </div>
            <button type="button" className="jt-btn-salvar" onClick={aplicarDescontoAdmin} disabled={salvandoDesconto}>{salvandoDesconto ? "Aplicando..." : "Aplicar cupom"}</button>
          </div>
        </div>
      )}

      {/* Modal Detalhes (já existia) */}
      {pedidoSelecionado && (
        <div className="jt-modal-overlay">
          <div className="jt-modal-content">
            <FaTimes className="jt-close-icon" onClick={fecharModal} />
            <h2>Detalhes do Pedido</h2>
            <p>
              <strong>Nome do Vaqueiro:</strong> {pedidoSelecionado.nomeVaqueiro || "Não informado"}
            </p>
            <p>
              <strong>Senhas:</strong>{" "}
              {pedidoSelecionado.senhas && pedidoSelecionado.senhas.length > 0
                ? pedidoSelecionado.senhas.map((s) => s.numero).join(", ")
                : "Não informado"}
            </p>
            <p>
              <strong>Evento:</strong> {pedidoSelecionado.evento?.nome || "Não informado"}
            </p>
            <p>
              <strong>Categoria:</strong> {pedidoSelecionado.categoria || "Não informado"}
            </p>
            <p>
              <strong>Origem:</strong>
              <span className={`jt-origem-badge jt-origem-${obterOrigemPedido(pedidoSelecionado).classe}`}>
                {obterOrigemPedido(pedidoSelecionado).rotulo}
              </span>
            </p>
            <p>
              <strong>Forma(s) de Pagamento:</strong> {rotuloFormasPagamento(pedidoSelecionado)}
            </p>
            <p>
              <strong>Rateio:</strong> {rateioFormasPagamento(pedidoSelecionado)}
            </p>
            <p>
              <strong>Valor Pago:</strong> R$ {pedidoSelecionado.total?.toFixed(2) || "0.00"}
            </p>
            {Number(pedidoSelecionado.desconto?.valor || 0) > 0 && <p><strong>Desconto aplicado ({pedidoSelecionado.desconto.codigo}):</strong> R$ {Number(pedidoSelecionado.desconto.valor).toFixed(2)}</p>}
            <p>
              <strong>Status do Pagamento:</strong>{" "}
              {statusVisualPagamento(pedidoSelecionado)}
            </p>
            {pedidoSelecionado.solicitacaoReembolso?.status && (
              <div style={{ margin: "14px 0", padding: "13px 15px", borderRadius: 10, background: "rgba(243,156,18,.12)", border: "1px solid rgba(243,156,18,.28)" }}>
                <p style={{ margin: 0 }}>
                  <strong>Solicitação de reembolso:</strong>{" "}
                  {pedidoSelecionado.solicitacaoReembolso.status === "solicitado_suporte"
                    ? "Aguardando confirmação após estorno manual"
                    : pedidoSelecionado.solicitacaoReembolso.status === "concluido"
                      ? "Reembolso concluído"
                      : pedidoSelecionado.solicitacaoReembolso.status}
                </p>
                {pedidoSelecionado.solicitacaoReembolso.status === "solicitado_suporte" && (
                  <button
                    type="button"
                    className="jt-btn-salvar"
                    style={{ marginTop: 12, background: "#f39c12" }}
                    onClick={() => confirmarReembolsoManual(pedidoSelecionado)}
                  >
                    Confirmar estorno concluído e liberar senhas
                  </button>
                )}
              </div>
            )}

            <h3>🐴 Dados do Vaqueiro</h3>
            <p>
              <strong>Nome do Vaqueiro:</strong> {pedidoSelecionado.nomeVaqueiro || "Não informado"}
            </p>
            {pedidoSelecionado.apelido && (
              <p>
                <strong>Apelido (nome na pista):</strong> {pedidoSelecionado.apelido}
              </p>
            )}
            <p>
              <strong>Representação:</strong> {pedidoSelecionado.representacao || "Não informado"}
            </p>
            <p>
              <strong>Cidade:</strong> {pedidoSelecionado.cidade || "Não informado"}
            </p>

            <h3>🏇 Informações do Cavalo</h3>
            <p>
              <strong>Cavalo de Puxada:</strong> {pedidoSelecionado.cavaloPuxar || "Não informado"}
            </p>
            <p>
              <strong>Cavalo de Esteira:</strong> {pedidoSelecionado.cavaloEsteira || "Não informado"}
            </p>
            <p>
              <strong>Nome do Esteira:</strong> {pedidoSelecionado.nomeEsteira || "Não informado"}
            </p>

            <h3>🔢 Senhas Adquiridas</h3>
            {pedidoSelecionado.senhas && pedidoSelecionado.senhas.length > 0 ? (
              <ul>
                {pedidoSelecionado.senhas.map((senha, index) => (
                  <li key={index}>
                    <strong>Senha {index + 1}:</strong> {senha.numero} - <strong>Status:</strong> {senha.status}
                    {!["cancelada", "disponivel"].includes(String(senha.status || "").toLowerCase()) && pedidoSelecionado.status !== "cancelado" && (
                      <button type="button" onClick={() => cancelarSenhaIndividual(pedidoSelecionado, senha)} style={{ marginLeft: 10, color: "#fff", background: "#b83232", border: 0, borderRadius: 5, padding: "5px 9px", cursor: "pointer" }}>
                        Cancelar esta senha
                      </button>
                    )}
                    {senha.valorCancelado > 0 && <small style={{ display: "block", opacity: 0.8 }}>Composição cancelada: R$ {Number(senha.valorCancelado).toFixed(2)}{senha.motivoCancelamento ? ` · ${senha.motivoCancelamento}` : ""}</small>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>⚠ Nenhuma senha adquirida.</p>
            )}
          </div>
        </div>
      )}

      {/* Modal Editar Senhas (já existia) */}
      {pedidoEditSenhas && (
        <div className="jt-modal-overlay">
          <div className="jt-modal-content">
            <FaTimes className="jt-close-icon" onClick={fecharModalEditarSenhas} />
            <h2>Editar Números das Senhas</h2>

            <p>
              <strong>Pedido:</strong> {pedidoEditSenhas._id}
            </p>
            <p>
              <strong>Evento:</strong> {pedidoEditSenhas.evento?.nome || "-"}
            </p>
            <p>
              <strong>Categoria:</strong> {pedidoEditSenhas.categoria || "-"}
            </p>

            <div className="jt-edicao-form" style={{ marginTop: 12 }}>
              {(novasSenhas || []).map((valor, idx) => (
                <div key={idx} className="jt-form-group">
                  <label>Senha {idx + 1}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={valor}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, "")
                      const clone = [...novasSenhas]
                      clone[idx] = v
                      setNovasSenhas(clone)
                    }}
                    className="jt-input"
                    placeholder="Número"
                  />
                </div>
              ))}
            </div>

            {erroSenhas && <div style={{ color: "red", marginTop: 8 }}>{erroSenhas}</div>}

            <button className="jt-btn-salvar" onClick={salvarNovasSenhas}>
              Salvar Números
            </button>
          </div>
        </div>
      )}

{/* Modal Editar Informações (atualizado para suportar dados por senha) */}
{pedidoEditando && (
  <div className="jt-modal-overlay">
    <div className="jt-modal-content">
      <FaTimes className="jt-close-icon" onClick={fecharModalEdicao} />
      <h2>Editar Informações do Pedido</h2>

      {/* 🔹 Parte 1: Dados gerais (para pedidos antigos e como padrão) */}
      <h3>Dados Gerais do Pedido</h3>
      <div className="jt-edicao-form">
        {[
          { label: "Nome do Vaqueiro", campo: "nomeVaqueiro" },
          { label: "Apelido (nome na pista)", campo: "apelido" },
          { label: "CPF", campo: "cpf" },
          { label: "Nome do Esteira", campo: "nomeEsteira" },
          { label: "Cavalo de Puxar", campo: "cavaloPuxar" },
          { label: "Cavalo de Esteira", campo: "cavaloEsteira" },
          { label: "Representação", campo: "representacao" },
          { label: "Cidade", campo: "cidade" },
        ].map(({ label, campo }) => (
          <div key={campo} className="jt-form-group">
            <label>{label}</label>
            <input
              type="text"
              value={pedidoEditando[campo] || ""}
              onChange={(e) =>
                setPedidoEditando({
                  ...pedidoEditando,
                  [campo]: e.target.value,
                })
              }
              className="jt-input"
            />
          </div>
        ))}
      </div>

      {/* 🔹 Parte 2: Dados por senha (para pedidos novos, ou pra diferenciar senhas) */}
      {Array.isArray(pedidoEditando.senhas) &&
        pedidoEditando.senhas.length > 0 && (
          <>
            <h3 style={{ marginTop: 30 }}>Dados Individuais por Senha</h3>
            <p style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
              Para pedidos antigos, os campos abaixo já vêm preenchidos com os
              dados gerais. Você pode alterar alguma senha específica se
              precisar.
            </p>

            {pedidoEditando.senhas.map((senha, index) => (
              <div
                key={senha._id || senha.numero || index}
                style={{
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <strong>Senha {senha.numero}</strong>

                <div className="jt-edicao-form" style={{ marginTop: 10 }}>
                  {[
                    { label: "Apelido da Senha", campo: "apelido" },
                    { label: "Nome do Esteira", campo: "nomeEsteira" },
                    { label: "Cavalo de Puxar", campo: "cavaloPuxar" },
                    { label: "Cavalo de Esteira", campo: "cavaloEsteira" },
                    { label: "Representação", campo: "representacao" },
                    { label: "Cidade", campo: "cidade" },
                  ].map(({ label, campo }) => (
                    <div key={campo} className="jt-form-group">
                      <label>{label}</label>
                      <input
                        type="text"
                        value={
                          senha.dadosVaqueiro?.[campo] ??
                          "" /* sempre existe por causa do abrirModalEdicao */
                        }
                        onChange={(e) =>
                          handleSenhaCampoChange(
                            index,
                            campo,
                            e.target.value
                          )
                        }
                        className="jt-input"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

      <button
        className="jt-btn-salvar"
        onClick={async () => {
          try {
            const token = localStorage.getItem("token");

            // monta payload de dados por senha
            const senhasDados = Array.isArray(pedidoEditando.senhas)
              ? pedidoEditando.senhas.map((s) => ({
                  numero: s.numero,
                  apelido: s.dadosVaqueiro?.apelido || s.apelido || "",
                  dadosVaqueiro: {
                    apelido: s.dadosVaqueiro?.apelido || s.apelido || "",
                    nomeEsteira: s.dadosVaqueiro?.nomeEsteira || "",
                    cavaloPuxar: s.dadosVaqueiro?.cavaloPuxar || "",
                    cavaloEsteira: s.dadosVaqueiro?.cavaloEsteira || "",
                    representacao: s.dadosVaqueiro?.representacao || "",
                    cidade: s.dadosVaqueiro?.cidade || "",
                  },
                }))
              : [];

            await axios.put(
              `https://api.ticketdovaqueiro.com/api/pedidos/atualizar-infos/${pedidoEditando._id}`,
              {
                nomeVaqueiro: pedidoEditando.nomeVaqueiro,
                apelido: pedidoEditando.apelido || null,  // 🔹 NOVO
                cpf: pedidoEditando.cpf,
                nomeEsteira: pedidoEditando.nomeEsteira,
                cavaloPuxar: pedidoEditando.cavaloPuxar,
                cavaloEsteira: pedidoEditando.cavaloEsteira,
                representacao: pedidoEditando.representacao,
                cidade: pedidoEditando.cidade,
                senhasDados,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            alert("Pedido atualizado com sucesso!");
            fecharModalEdicao();

            // atualiza state local
            setPedidos((prev) =>
              prev.map((p) =>
                p._id === pedidoEditando._id ? { ...p, ...pedidoEditando } : p
              )
            );
          } catch (err) {
            console.error("Erro ao atualizar pedido:", err);
            alert("Erro ao atualizar.");
          }
        }}
      >
        Salvar Alterações
      </button>
    </div>
  </div>
)}


      {/* Modal Vale */}
      {pedidoVale && (
        <div className="jt-modal-overlay">
          <div className="jt-modal-content">
            <FaTimes className="jt-close-icon" onClick={fecharModalVale} />
            {pedidoPossuiVale(pedidoVale) ? (
              <>
                <h2>Remover Vale</h2>
                <p style={{ marginTop: 0 }}>
                  Esta operação substitui somente a parcela em vale, sem alterar o total do pedido.
                </p>
                <p>
                  <strong>Valor em vale comum:</strong>{" "}
                  R$ {valorEmValeDoPedido(pedidoVale).toFixed(2)}
                </p>
                {valorValeParqueRestanteDoPedido(pedidoVale) > 0 && (
                  <p>
                    <strong>Vale do parque não faturado:</strong>{" "}
                    R$ {valorValeParqueRestanteDoPedido(pedidoVale).toFixed(2)}
                  </p>
                )}

                <div className="jt-form-group">
                  <label>Qual saldo deseja converter?</label>
                  <select
                    className="jt-input"
                    value={tipoValeRemover}
                    onChange={(e) => setTipoValeRemover(e.target.value)}
                  >
                    <option value="comum">Vale comum / cliente pagou</option>
                    <option value="parque">Vale do parque / reduzir bonificação</option>
                  </select>
                </div>

                <div className="jt-form-group">
                  <label>Valor pago agora</label>
                  <input
                    className="jt-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Deixe vazio para converter todo o saldo escolhido"
                    value={valorRemoverVale}
                    onChange={(e) => setValorRemoverVale(e.target.value)}
                  />
                </div>

                <div className="jt-form-group">
                  <label>Nova forma de pagamento</label>
                  <select
                    className="jt-input"
                    value={novaFormaVale}
                    onChange={(e) => setNovaFormaVale(e.target.value)}
                  >
                    <option value="pix">Pix</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="cartao">Cartão</option>
                    <option value="boleto">Boleto</option>
                    <option value="cheque">Cheque</option>
                    <option value="transferencia">Transferência</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>

                <div className="jt-form-group">
                  <label>Motivo / observação (opcional)</label>
                  <textarea
                    className="jt-input"
                    placeholder="Ex.: cliente acertou o valor via Pix."
                    value={motivoRemoverVale}
                    onChange={(e) => setMotivoRemoverVale(e.target.value)}
                  />
                </div>

                <button className="jt-btn-salvar" onClick={salvarVale}>
                  Confirmar remoção do vale
                </button>
              </>
            ) : (
              <>
                <h2>Criar Vale</h2>
                <div className="jt-form-group">
                  <label>Tipo de vale</label>
                  <select
                    className="jt-input"
                    value={tipoValeCriar}
                    onChange={(e) => setTipoValeCriar(e.target.value)}
                  >
                    <option value="comum">Vale comum / abatimento financeiro</option>
                    <option value="parque">Vale do parque / bonificação não faturada</option>
                  </select>
                </div>

                {tipoValeCriar === "parque" && (
                  <>
                    <p style={{ marginTop: 0 }}>
                      <strong>Valor atual do pedido:</strong>{" "}
                      R$ {(Number(pedidoVale?.total) || 0).toFixed(2)}
                    </p>
                    <div className="jt-form-group">
                      <label>Valor que será bonificado pelo parque</label>
                      <input
                        className="jt-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Deixe vazio para zerar o pedido inteiro"
                        value={valorValeParque}
                        onChange={(e) => setValorValeParque(e.target.value)}
                      />
                      <small>
                        Ex.: pedido de R$ 900,00 com bonificação de R$ 450,00 ficará com R$ 450,00 faturado e R$ 450,00 como vale do parque.
                      </small>
                    </div>
                  </>
                )}

                <div className="jt-form-group">
                  <label>Descrição do Vale</label>
                  <textarea
                    className="jt-input"
                    placeholder="Ex.: Vale liberado por Antonio, Recebeu o pagamento em mãos..."
                    value={valeDescricao}
                    onChange={(e) => setDescricaoVale(e.target.value)}
                  />
                </div>

                <button className="jt-btn-salvar" onClick={salvarVale}>
                  Criar Vale
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {/* ========================================================= */}
      {/* MODAL: TROCA DE CATEGORIA (UPGRADE) */}
      {/* ========================================================= */}
      {pedidoTrocaCategoria && (
        <div className="jt-modal-overlay">
          <div className="jt-modal-content" style={{ maxWidth: "400px" }}>
            <FaTimes className="jt-close-icon" onClick={() => setPedidoTrocaCategoria(null)} />
            <h2 style={{ color: "#f39c12", marginBottom: "5px" }}><FaExchangeAlt /> Trocar Categoria</h2>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
              Pedido Atual: <strong>{pedidoTrocaCategoria.categoria}</strong><br/>
              Total Atual: <strong style={{color: "#e74c3c"}}>R$ {pedidoTrocaCategoria.total}</strong>
            </p>

            <div className="jt-form-group">
              <label>Selecione a Nova Categoria:</label>
              <select 
                className="jt-input" 
                value={novaCategoriaSelecionada} 
                onChange={(e) => setNovaCategoriaSelecionada(e.target.value)}
              >
                <option value="">-- Selecione --</option>
                {eventos
                  .find(e => {
                    const idEventoPedido = pedidoTrocaCategoria.evento?._id || pedidoTrocaCategoria.evento;
                    return e._id === idEventoPedido;
                  })
                  ?.categorias.map(cat => {
                    // Pega o preço da 1ª Senha para usar como valor de exibição base
                    const configSenha1 = cat.senhas?.find(s => s.numero === 1);
                    const precoBase = configSenha1 ? Number(configSenha1.preco) : 0;

                    return (
                      <option key={cat.nome} value={cat.nome}>
                        {cat.nome} (1ª Senha: R$ {precoBase.toFixed(2)})
                      </option>
                    )
                  })}
              </select>
            </div>

            <button 
              className="jt-btn-salvar" 
              style={{ background: "#f39c12", width: "100%", marginTop: "15px" }} 
              onClick={salvarTrocaCategoria}
            >
              Confirmar e Recalcular
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendasAdmin
